/**
 * Helper functions for MapUnit shapes
 */

import { getContrastTextColorValue } from '../../../utils/common';

/**
 * Generates a unique pattern ID based on fill color and overlay type
 * Must match the ID format used in MapUnitPatterns.js
 */
export const getCompositePatternId = (fillColor, overlayColor) => {
  const sanitizedFill = fillColor.replace(/[^a-zA-Z0-9]/g, '');
  return `unitOverlayPattern-${sanitizedFill}-${overlayColor}`;
};

/**
 * Generates SVG markup string for all unit shapes
 * Uses composite patterns for overlays - single shape with fill color baked into pattern.
 * Requires MapUnitPatterns component in SVG <defs> to generate composite patterns.
 * @param {Array} units - Array of unit objects
 * @param {string} unitFill - Default fill color for disabled units
 * @param {string} unitStroke - Default stroke color
 * @param {boolean} platformShowOccupancy - Whether to show occupancy overlays
 * @returns {string} SVG markup string
 */
export const generateUnitShapesMarkup = (units, unitFill, unitStroke, platformShowOccupancy = false) => {
  if (!units || units.length === 0) return '';

  const shapeStrings = units.map((unit) => {
    const {
      type, attributes, info, displayMode,
    } = unit;
    if (!type || !attributes) return '';

    const id = attributes.id || '';
    const keyColor = info?.key_color || unitFill;
    const status = info?.residence_status;

    // Compute fill/stroke based on displayMode
    const { fill, stroke } = getFillAndStroke(displayMode, keyColor, unitStroke, unitFill);

    // Check if this unit needs an overlay
    const showOverlay = shouldShowOverlay(status, platformShowOccupancy, displayMode);
    // Inactive units always use black overlay for consistent appearance
    // Active units use pre-computed contrast_overlay from API, fallback to computed value
    const overlayType = showOverlay
      ? (displayMode === 'inactive' ? 'black' : (info?.contrast_overlay || getContrastTextColorValue(fill) || 'white'))
      : null;

    // Build geometry attributes string (reused for base and overlay)
    const geomParts = [];
    if (attributes.d) geomParts.push(`d="${attributes.d}"`);
    if (attributes.points) geomParts.push(`points="${attributes.points}"`);
    if (attributes.x !== undefined) geomParts.push(`x="${attributes.x}"`);
    if (attributes.y !== undefined) geomParts.push(`y="${attributes.y}"`);
    if (attributes.width !== undefined) geomParts.push(`width="${attributes.width}"`);
    if (attributes.height !== undefined) geomParts.push(`height="${attributes.height}"`);
    if (attributes.r !== undefined) geomParts.push(`r="${attributes.r}"`);
    if (attributes.cx !== undefined) geomParts.push(`cx="${attributes.cx}"`);
    if (attributes.cy !== undefined) geomParts.push(`cy="${attributes.cy}"`);
    if (attributes.transform) geomParts.push(`transform="${attributes.transform}"`);
    const geomStr = geomParts.join(' ');

    // Single shape - uses composite pattern if overlay needed, otherwise solid fill
    // Composite patterns have the fill color baked in, so only one shape is needed
    if (showOverlay && overlayType) {
      const patternId = getCompositePatternId(fill, overlayType);
      return `<${type} data-svg-id="${id}" class="unit unit--${displayMode || 'disabled'}" fill="url(#${patternId})" stroke="${stroke}" ${geomStr} />`;
    }

    // No overlay - use solid fill
    return `<${type} data-svg-id="${id}" class="unit unit--${displayMode || 'disabled'}" fill="${fill}" stroke="${stroke}" ${geomStr} />`;
  });

  return shapeStrings.join('');
};

/**
 * Determines fill and stroke colors based on display mode
 * @param {string} displayMode - Display mode ('active', 'inactive', 'disabled')
 * @param {string} keyColor - Unit's key color
 * @param {string} unitStroke - Default stroke color
 * @param {string} unitFill - Default fill color
 * @returns {Object} Fill and stroke colors
 */
export const getFillAndStroke = (displayMode, keyColor, unitStroke, unitFill) => {
  switch (displayMode) {
    case 'active':
      return { fill: keyColor || '#000000', stroke: unitStroke };
    case 'inactive':
      return { fill: unitStroke, stroke: keyColor || '#000000' };
    case 'disabled':
      return { fill: unitFill, stroke: unitStroke };
    default:
      return { fill: unitFill, stroke: unitStroke };
  }
};

/**
 * Determines if overlay should be shown
 * @param {Object} status - Unit status object
 * @param {boolean} platformShowOccupancy - Platform occupancy flag
 * @param {string} displayMode - Display mode
 * @returns {boolean} Whether to show overlay
 */
export const shouldShowOverlay = (status, platformShowOccupancy, displayMode) => (
  status?.consider_unoccupied !== '1' && platformShowOccupancy && displayMode !== 'disabled'
);
